import type {
  HealthcareSessionId,
  HealthcareUserId,
  TobaccoUse,
} from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { removeEmptyKeys } from '@hc-frontend/core-utils-object';
import { v1 } from '@hc-frontend/services-lead-storage';

import type {
  HandleContextualTrafficRepositoryResult,
  Prospect,
} from '../../domain';

function transformDob(date: Date) {
  return date.toISOString().slice(0, -1);
}

function transformTobacco(value?: TobaccoUse | null) {
  const map: Record<TobaccoUse, boolean> = {
    YES: true,
    NO: false,
  };

  return value ? map[value] : undefined;
}

export async function updateProspect(
  baseURL: string,
  prospect: Prospect,
  sessionId: HealthcareSessionId,
  userId: HealthcareUserId,
): HandleContextualTrafficRepositoryResult {
  try {
    const family = prospect.children.map(
      (member, index) =>
        removeEmptyKeys({
          journeyMemberId: `Child_${index + 1}`,
          relationship: member.memberType,
          firstName: member.firstName,
          lastName: member.lastName,
          gender: member.gender,
          tobaccoUser: transformTobacco(member.tobaccoUse),
          dateOfBirth: member?.dateOfBirth
            ? transformDob(member?.dateOfBirth)
            : undefined,
        }) as v1.FamilyDto,
    );

    if (prospect.spouse) {
      family.push(
        removeEmptyKeys({
          journeyMemberId: 'Spouse',
          relationship: prospect.spouse.memberType,
          firstName: prospect.spouse.firstName,
          lastName: prospect.spouse.lastName,
          gender: prospect.spouse.gender,
          tobaccoUser: transformTobacco(prospect.spouse.tobaccoUse),
          dateOfBirth: prospect.spouse.dateOfBirth
            ? transformDob(prospect.spouse?.dateOfBirth)
            : undefined,
        }) as v1.FamilyDto,
      );
    }

    const body: v1.UpdateProspectBaseDto = {
      sessionId,
      userId,
      firstName: prospect.applicant?.firstName,
      lastName: prospect.applicant?.lastName,
      dateOfBirth: prospect.applicant?.dateOfBirth
        ? transformDob(prospect.applicant?.dateOfBirth)
        : undefined,
      state: prospect.location?.stateAcronym,
      email: prospect.contactInformation?.email,
      householdSize: prospect.householdSize,
      gender: prospect.applicant?.gender,
      householdIncome: prospect.householdIncome,
      address: prospect.contactInformation?.address,
      city: prospect.location?.city,
      county: prospect.location?.county,
      countyFips: prospect.location?.countyFips,
      zip: prospect.location?.zipCode,
      tobaccoUser: transformTobacco(prospect.applicant?.tobaccoUse),
      phoneNumber: prospect.contactInformation?.phoneNumber,
      family,
    };

    const response = await v1.updateProspectService(baseURL, body);

    if (response.result !== 'success') {
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToHandleContextualTraffic',
            rootCause: new Error('Failed to update prospect journey.'),
          },
        ],
      });
    }

    return createSuccessOperationResult(prospect);
  } catch (error) {
    return createErrorOperationResult({
      errors: [
        {
          code: 'failedToHandleContextualTraffic',
          rootCause: error as Error,
        },
      ],
    });
  }
}
